import axios from 'axios';
import {
  Configuration as MainAPIConfiguration,
  AuthorisedRepresentativeApi,
  DocumentApi,
  EntityApi,
  EntityDetailApi,
  EntityDetailCategoryApi,
  EntityDetailConfigApi,
  EntityDetailValueApi,
  FeeApi,
  FormApi,
  NoteApi,
  OrganisationApi,
  OrganisationSettingApi,
  RoleUserOrgAssociationApi,
  ScreenApi,
  TimelineApi,
  UserConfigApi,
  WorkOrderApi,
  FormActionAssociationApi,
  BillableEventApi,
  EntityTransferApi,
  ImportBatchApi,
  EntityDetailEnhancerApi,
  ValidationApi as ValidationRuleApi,
  RuleExecutionApi,
  RuleConfigurationApi,
  RoleApi,
  SolrSyncApi,
} from '@/apiClient';

import {
  Configuration as AuthAPIConfiguration,
  UserApi,
  UserProfileApi,
} from '@/authApiClient';

import { getCurrentCorrelationId } from '@/lib/correlationId';
import { SELECTED_ORG_ID } from '@/configs/storageKeys';

/**
 * Creates API clients that route through server-side proxies.
 * No tokens are sent from the browser -- the proxy handles auth.
 */
export const createApiClients = () => {
  const selectedOrganisationId =
    typeof window !== 'undefined'
      ? (sessionStorage.getItem(SELECTED_ORG_ID) ??
        localStorage.getItem(SELECTED_ORG_ID))
      : undefined;

  const headers: Record<string, string> = {
    'X-Correlation-ID': getCurrentCorrelationId() || '',
    'X-Request-ID': getCurrentCorrelationId() || '',
  };

  if (selectedOrganisationId) {
    headers['X-Organisation-Id'] = selectedOrganisationId;
  }

  // All API calls route through server-side proxy routes (same-origin).
  // The proxy validates the session cookie and adds service auth headers.
  const mainAxiosInstance = axios.create({
    baseURL: '/api/proxy/core',
    headers,
    withCredentials: true,
  });

  const authAxiosInstance = axios.create({
    baseURL: '/api/proxy/auth',
    headers,
    withCredentials: true,
  });

  const elsaAxiosInstance = axios.create({
    baseURL: '/api/proxy/elsa',
    timeout: 5000,
    headers,
    withCredentials: true,
  });

  // Match initial headers: session then localStorage (interceptor used to be session-only).
  const addOrgHeader = (config: any) => {
    const orgId =
      typeof window !== 'undefined'
        ? (sessionStorage.getItem(SELECTED_ORG_ID) ??
          localStorage.getItem(SELECTED_ORG_ID))
        : undefined;
    if (orgId) {
      config.headers['X-Organisation-Id'] = orgId;
    }
    return config;
  };

  mainAxiosInstance.interceptors.request.use(addOrgHeader);
  authAxiosInstance.interceptors.request.use(addOrgHeader);
  elsaAxiosInstance.interceptors.request.use(addOrgHeader);

  const apiConfig = new MainAPIConfiguration({
    basePath: '/api/proxy/core',
    baseOptions: { headers, withCredentials: true },
  });

  const authConfig = new AuthAPIConfiguration({
    basePath: '/api/proxy/auth',
    baseOptions: { headers, withCredentials: true },
  });

  return {
    axiosInstance: mainAxiosInstance,
    elsaApi: elsaAxiosInstance,
    authorisedRepresentativeApi: new AuthorisedRepresentativeApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    billableEventApi: new BillableEventApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    validationRuleApi: new ValidationRuleApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    ruleExecutionApi: new RuleExecutionApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    documentApi: new DocumentApi(apiConfig, undefined, mainAxiosInstance),
    entityApi: new EntityApi(apiConfig, undefined, mainAxiosInstance),
    entityDetailApi: new EntityDetailApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityDetailCategoryApi: new EntityDetailCategoryApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    roleApi: new RoleApi(apiConfig, undefined, mainAxiosInstance),
    entityDetailConfigApi: new EntityDetailConfigApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityDetailEnhancerApi: new EntityDetailEnhancerApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityDetailValueApi: new EntityDetailValueApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    entityTransferApi: new EntityTransferApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    feeApi: new FeeApi(apiConfig, undefined, mainAxiosInstance),
    formApi: new FormApi(apiConfig, undefined, mainAxiosInstance),
    formActionAssociationApi: new FormActionAssociationApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    importBatchApi: new ImportBatchApi(apiConfig, undefined, mainAxiosInstance),
    noteApi: new NoteApi(apiConfig, undefined, mainAxiosInstance),
    organisationApi: new OrganisationApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    organisationSettingApi: new OrganisationSettingApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    roleUserOrgAssociationApi: new RoleUserOrgAssociationApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    ruleConfigurationApi: new RuleConfigurationApi(
      apiConfig,
      undefined,
      mainAxiosInstance,
    ),
    screenApi: new ScreenApi(apiConfig, undefined, mainAxiosInstance),
    solrSyncApi: new SolrSyncApi(apiConfig, undefined, mainAxiosInstance),
    timelineApi: new TimelineApi(apiConfig, undefined, mainAxiosInstance),
    userConfigApi: new UserConfigApi(apiConfig, undefined, mainAxiosInstance),
    workOrderApi: new WorkOrderApi(apiConfig, undefined, mainAxiosInstance),
    userApi: new UserApi(authConfig, undefined, authAxiosInstance),
    userProfileApi: new UserProfileApi(
      authConfig,
      undefined,
      authAxiosInstance,
    ),
  };
};
